import { useDataTableUsingUrl } from '@juvare/common-data-table-ui/DataTable';
import { useFilterConfig } from '@juvare/common-data-table-ui/FiltersBar';
import { showConfirmDialog } from '@juvare/common-web-ui/DialogHelper';
import { launchBulkAction } from '@juvare/platform-sdk-react/ui/features/bulk-actions';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import { Box, FormControlLabel, Switch, Typography } from '@mui/material';
import React from 'react';
import { useSearchParams } from 'react-router';
// eslint-disable-next-line no-restricted-imports
import { deleteAlertsBulkActionType } from '../../../../../backend/features/alerts/bulk-actions';
import alertImage from '../../../../assets/alertImage.svg';
import { showAlertNotificationRowDialog } from '../../../../components/AlertNotificationRowDialog';
import { useAlertUiTranslation } from '../../../../hooks';
import { getAlertBulkActionParams } from '../../../../utils';
import { useAlertRefreshButton, useAlertsTable, useCreateTemplateButton } from '../../hooks';
import { AlertView } from '../AlertView';
import { AlertsList } from '../AlertsList';
export const AlertsListTemplate = () => {
    var _a;
    const { t } = useAlertUiTranslation();
    const [searchParams, setSearchParams] = useSearchParams();
    const filterConfig = useFilterConfig((builder) => {
        const search = builder.quickSearchFilter({ dataPath: 'searchText', label: t('common.search') });
        const createdBy = builder.quickFilterSelect({
            dataPath: 'createdByMe',
            label: t('pages.tables.filters.createdBy'),
            values: [{ label: t('pages.tables.filters.anyone') }, { value: true, label: t('pages.tables.filters.me') }],
            widget: 'form.radio',
        });
        const notificationType = builder.quickFilterMultiSelect({
            dataPath: 'notificationType',
            label: t('pages.tables.filters.notificationType'),
            valueProviderType: 'classifier.messageType',
            widget: 'form.checkboxList',
        });
        const sharedWith = builder.quickFilterMultiSelect({
            dataPath: 'sharedWith',
            label: t('pages.tables.filters.sharedWith'),
            values: [
                { value: 'user', label: t('common.sharedWith.user') },
                { value: 'anyone', label: t('common.sharedWith.anyone') },
                { value: 'owner', label: t('common.sharedWith.owner') },
                { value: 'not-shared', label: t('common.sharedWith.not-shared') },
            ],
            widget: 'form.multiSelect',
        });
        builder.allFiltersPanel({
            children: [
                search,
                builder.allFiltersWidget({
                    type: 'form.text',
                    key: 'title',
                    label: t('common.templateName'),
                    emptyAsNull: true,
                }),
                sharedWith,
                createdBy,
                notificationType,
            ],
        });
        builder.allFiltersPanel({
            label: t('common.dateSent'),
            children: [
                builder.allFiltersWidget({
                    type: 'form.dateTimePicker',
                    key: 'dateSentFrom',
                    dateLabel: t('common.fromDate'),
                    timeLabel: t('common.fromTime'),
                }),
                builder.allFiltersWidget({
                    type: 'form.dateTimePicker',
                    key: 'dateSentTo',
                    dateLabel: t('common.toDate'),
                    timeLabel: t('common.toTime'),
                }),
            ],
        });
    }, [t]);
    const handleRowClickDialog = (alertRowId) => {
        showAlertNotificationRowDialog({
            type: 'alert',
            onClearActiveRow: clearActiveRow,
            onReload: reload,
            label: t('common.backToTemplates'),
            rowId: alertRowId,
        });
    };
    const pinned = searchParams.get('pinned') === 'true';
    const staticFilterData = { statuses: ['template'], pinned: pinned ? true : undefined };
    const { dataTableProps, appliedFilterData, clearActiveRow, reload } = useAlertsTable({
        useDataTableFunction: useDataTableUsingUrl,
        staticFilterData: staticFilterData,
        filterConfig,
        defaultSortColumnKey: 'createdAt',
        handleRowClickDialog,
    });
    const selectedAlertIds = (_a = dataTableProps.selectedRowIds) !== null && _a !== void 0 ? _a : [];
    const handleDelete = () => {
        const count = selectedAlertIds.length > 0 ? selectedAlertIds.length : dataTableProps.filteredRowCount;
        showConfirmDialog({
            title: t('deleteTemplateAlertsDialog.title', { count }),
            content: t('deleteTemplateAlertsDialog.message', { count }),
            confirmLabel: t('common.delete'),
            onConfirm: () => {
                launchBulkAction({
                    type: deleteAlertsBulkActionType,
                    title: t('deleteTemplateAlertsDialog.progress', { count }),
                    params: getAlertBulkActionParams(dataTableProps, appliedFilterData, staticFilterData),
                    onClose: () => {
                        var _a;
                        clearActiveRow();
                        (_a = dataTableProps.onDeselectAllRows) === null || _a === void 0 ? void 0 : _a.call(dataTableProps);
                        reload();
                    },
                });
            },
        });
    };
    const actions = [
        {
            icon: React.createElement(DeleteOutlinedIcon, null),
            label: t('common.delete'),
            onClick: handleDelete,
        },
    ];
    const mainAction = useCreateTemplateButton();
    const secondaryAction = useAlertRefreshButton(() => reload());
    return (React.createElement(AlertsList, Object.assign({}, dataTableProps, { title: t('pages.tables.tabs.templates'), noAlertsLabel: t('common.thereAreNoTemplateAlerts'), localActions: actions, mainAction: mainAction, secondaryAction: secondaryAction, onReloadList: reload, customHeaderElement: React.createElement(FormControlLabel, { sx: { mt: '-8px', mb: '-8px' }, label: t('common.showPinnedOnly'), control: React.createElement(Switch, { checked: pinned, onChange: (_event, checked) => {
                    const newSearchParams = new URLSearchParams(searchParams);
                    if (checked) {
                        newSearchParams.set('pinned', 'true');
                    }
                    else {
                        newSearchParams.delete('pinned');
                    }
                    setSearchParams(newSearchParams, { replace: true });
                } }) }) }), dataTableProps.activeRowId ? (React.createElement(AlertView, { id: dataTableProps.activeRowId, onReload: (clearActiveAlert) => {
            if (clearActiveAlert) {
                clearActiveRow();
            }
            reload();
        } })) : (React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2, px: 2, py: '180px', alignItems: 'center' } },
        React.createElement("img", { src: alertImage, alt: '' }),
        React.createElement(Typography, { variant: 'subtitle1' }, t('common.countTemplateAlertsSelected', { count: selectedAlertIds.length }))))));
};
